// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type {
  AutoUpdateOptions,
  DetectOverflowOptions,
  FlipOptions,
  Middleware,
  Placement,
  ReferenceElement,
  ShiftOptions,
  Strategy,
} from '@floating-ui/dom'
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  limitShift,
  offset,
  shift,
} from '@floating-ui/dom'
import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Host,
  Method,
  Prop,
  Watch,
} from '@stencil/core'
import type { Split } from 'type-fest'

import type { OffsetOptions } from '../../helpers'
import {
  ignorePromise,
  invariant,
  isTouchDevice,
  propertyToPx,
  resolveTarget,
  roundByDPR,
} from '../../helpers'

let autoIncrementingId = 1

export type TriggerEvent = Extract<
  keyof GlobalEventHandlersEventMap,
  'click' | 'focus' | 'focusin' | 'pointerenter'
>

/**
 * @slot Content to display in the dropdown.
 * @see https://quartz.se.com/build/components/tooltip
 */
@Component({
  tag: 'qds-tooltip',
  shadow: true,
  styleUrl: 'tooltip.css',
})
export class Tooltip implements ComponentInterface {
  /**
   * Options to pass to Floating UI's
   * [`autoUpdate()` function](https://floating-ui.com/docs/autoUpdate).
   *
   * @see https://floating-ui.com/docs/autoupdate#options
   */
  @Prop() public readonly autoUpdateOptions?: AutoUpdateOptions

  /**
   * Prevents the tooltip from being shown by user interaction. The tooltip can
   * still be displayed by calling the `show()` method.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * Options to pass to Floating UI's
   * [`flip` middleware](https://floating-ui.com/docs/flip).
   *
   * The [`crossAxis` option](https://floating-ui.com/docs/flip#crossaxis) is
   * set to the value of the `noShift` property by default for interoperability
   * with the [`shift` middleware](https://floating-ui.com/docs/shift).
   *
   * @see https://floating-ui.com/docs/flip#options
   */
  @Prop() public readonly flipOptions?: Partial<
    DetectOverflowOptions & FlipOptions
  >

  /**
   * Disable Floating UI's
   * [`flip` middleware](https://floating-ui.com/docs/flip).
   */
  @Prop() public readonly noFlip: boolean = false

  /**
   * Disable Floating UI's
   * [`shift` middleware](https://floating-ui.com/docs/shift).
   */
  @Prop() public readonly noShift: boolean = false

  /**
   * Options to pass to Floating UI's
   * [`offset` middleware](https://floating-ui.com/docs/offset).
   *
   * The [`mainAxis` option](https://floating-ui.com/docs/offset#mainaxis) is
   * not supported.
   *
   * @see https://floating-ui.com/docs/offset#options
   */
  @Prop() public readonly offsetOptions?: OffsetOptions

  /**
   * Where to place the tooltip relative to the `target`.
   *
   * @see https://floating-ui.com/docs/tutorial#placements
   */
  @Prop() public readonly placement: Placement = 'top'

  /**
   * Options to pass to Floating UI's
   * [`shift` middleware](https://floating-ui.com/docs/shift).
   *
   * @see https://floating-ui.com/docs/shift#options
   */
  @Prop() public readonly shiftOptions?: Partial<
    DetectOverflowOptions & ShiftOptions
  >

  /**
   * The CSS position strategy to use.
   *
   * @see https://floating-ui.com/docs/computeposition#strategy
   */
  @Prop() public readonly strategy?: Strategy

  /**
   * The trigger element that will trigger the tooltip. The target specified
   * can be any of the following:
   *
   * - A CSS selector string
   * - A reference to an [`Element`][] or [Virtual Element][]
   * - A function returning a reference to an [`Element`][],
   * [Virtual Element][], or a CSS selector string
   *
   * [`Element`]: https://developer.mozilla.org/docs/Web/API/Element
   * [Virtual Element]: https://floating-ui.com/docs/virtual-elements
   */
  @Prop() public readonly target?:
    | ReferenceElement
    | string
    | (() => ReferenceElement | string)

  /**
   * Determines the events that cause the tooltip to show. Multiple event names
   * are separated by spaces.
   */
  @Prop() public readonly trigger?: TriggerEvent[] | string =
    'pointerenter focus'

  /**
   * Emitted when the tooltip is closed.
   */
  @Event({ eventName: 'qdsClose', bubbles: false, cancelable: false })
  private readonly closeEmitter!: EventEmitter<void>

  @Element() private readonly host!: HTMLElement

  @AttachInternals() private readonly internals!: ElementInternals

  #arrow?: HTMLElement

  #arrowPadding = 0

  #cleanup?: () => void

  #clickedTooltip = false

  #isTouchDevice = false

  #listeners: {
    element: Element
    event: keyof GlobalEventHandlersEventMap
    listener: EventListenerOrEventListenerObject
  }[] = []

  #mainAxisDistance = 0

  #middleware?: Middleware[]

  #open = false

  /**
   * Ensure that `updateArrow()` is called only once internally as this is
   * expensive.
   */
  #opened = false

  #referenceElement?: ReferenceElement

  /**
   * Show the tooltip.
   */
  @Method()
  public async show(): Promise<void> {
    if (this.#open) return

    this.#open = true
    this.#setAriaExpanded()
    this.startAutoUpdate()

    if (!this.#opened) await this.updateArrow()

    this.#opened = true
  }

  /**
   * Close the tooltip.
   */
  @Method()
  public async close(): Promise<void> {
    if (!this.#open) return

    this.closeEmitter.emit()

    this.#open = false
    this.#setAriaExpanded()
    this.#clickedTooltip = false
    if (this.#cleanup) this.#cleanup()
  }

  /**
   * Update the tooltip's arrow.
   *
   * This method should typically be called when the Quartz platform was
   * changed.
   */
  @Method()
  public async updateArrow(): Promise<void> {
    const gap = propertyToPx(this.host, '--qds-accessory-gap-floating') ?? 0
    const height =
      propertyToPx(this.host, '--qds-signature-triangle-pointer-size-height') ??
      0
    const padding =
      propertyToPx(this.host, '--qds-accessory-border-radius') ?? 0
    this.#mainAxisDistance = gap + height
    this.#arrowPadding = padding

    await this.updateMiddleware()
  }

  @Watch('flipOptions')
  @Watch('noFlip')
  @Watch('noShift')
  @Watch('offsetOptions')
  @Watch('shiftOptions')
  protected async updateMiddleware(): Promise<void> {
    invariant(this.#arrow)

    this.#middleware = [
      offset({
        ...this.offsetOptions,
        mainAxis: this.#mainAxisDistance,
      }),
      !this.noFlip &&
        flip({
          crossAxis: this.noShift,
          ...this.flipOptions,
        }),
      !this.noShift &&
        shift({
          limiter: limitShift(),
          ...this.shiftOptions,
        }),
      arrow({
        element: this.#arrow,
        padding: this.#arrowPadding,
      }),
    ].filter((middleware): middleware is Middleware => middleware !== false)

    await this.updatePosition()
  }

  @Watch('role')
  protected updateAriaHasPopup(): void {
    this.#referenceElement = resolveTarget(this.target)

    if (
      this.#referenceElement instanceof globalThis.Element &&
      this.host.role === 'dialog'
    )
      this.#referenceElement.ariaHasPopup = 'dialog'
  }

  @Watch('target')
  private async updateReferenceElement(): Promise<void> {
    this.disconnectedCallback()

    this.#referenceElement = resolveTarget(this.target)

    if (!(this.#referenceElement instanceof globalThis.Element)) return

    this.updateEventListeners()
    this.host.id ||= `qds-tooltip-${autoIncrementingId}`
    this.#referenceElement.setAttribute('aria-describedby', this.host.id)
    if (this.host.role === 'dialog')
      this.#referenceElement.ariaHasPopup = 'dialog'
  }

  @Watch('trigger')
  private updateEventListeners(): void {
    if (!(this.#referenceElement instanceof globalThis.Element)) return

    const triggers = Array.isArray(this.trigger)
      ? this.trigger
      : ((this.trigger?.split(' ') as Split<TriggerEvent, ' '> | undefined) ??
        [])

    this.#removeEventListeners()

    if (this.#isTouchDevice)
      this.#on(this.#referenceElement, 'click', this.#boundReferenceClick)
    this.#on(document.documentElement, 'pointerdown', this.#boundPointerdown)
    this.#on(this.#referenceElement, 'keydown', this.#boundKeyDown)

    for (const trigger of triggers) {
      switch (trigger) {
        case 'click': {
          this.#on(this.#referenceElement, 'click', this.#boundReferenceClick)
          break
        }
        case 'focus':
        case 'focusin': {
          this.#on(this.#referenceElement, trigger, this.#boundFocus)
          this.#on(this.#referenceElement, 'focusout', this.#boundFocusout)
          this.#on(this.host, 'focusout', this.#boundFocusout)
          break
        }
        case 'pointerenter': {
          this.#on(
            this.#referenceElement,
            'pointerenter',
            this.#boundPointerenter,
          )
          this.#on(
            this.#referenceElement,
            'pointerleave',
            this.#boundPointerleave,
          )
          this.#on(this.host, 'pointerleave', this.#boundPointerleave)
          break
        }
        default:
      }
    }
  }

  @Watch('middleware')
  @Watch('placement')
  @Watch('strategy')
  private async updatePosition(): Promise<void> {
    invariant(this.#referenceElement)
    invariant(this.#arrow)

    const { x, y, middlewareData, placement, strategy } = await computePosition(
      this.#referenceElement,
      this.host,
      {
        middleware: this.#middleware,
        placement: this.placement,
        strategy: this.strategy,
      },
    )
    const [side] = placement.split('-') as Split<Placement, '-'>

    this.host.dataset.side = side
    this.host.style.position = strategy
    this.host.style.transform = `translate(${roundByDPR(x)}px,${roundByDPR(y)}px)`

    if (middlewareData.arrow === undefined) return

    invariant(this.#arrow)

    const { x: aX, y: aY, centerOffset } = middlewareData.arrow
    this.#arrow.dataset.side = side
    this.#arrow.style.display = centerOffset === 0 ? '' : 'none'
    this.#arrow.style.left = aX === undefined ? '' : `${aX}px`
    this.#arrow.style.top = aY === undefined ? '' : `${aY}px`
  }

  @Watch('autoUpdateOptions')
  private startAutoUpdate(): void {
    invariant(this.#referenceElement)

    if (this.#cleanup) this.#cleanup()
    this.#cleanup = autoUpdate(
      this.#referenceElement,
      this.host,
      () => {
        ignorePromise(this.updatePosition())
      },
      this.autoUpdateOptions,
    )
  }

  public async componentWillLoad(): Promise<void> {
    this.#isTouchDevice = isTouchDevice()
    this.host.hidden = true
    this.host.popover = 'manual'
    this.internals.role = 'tooltip'
    await this.updateReferenceElement()
    autoIncrementingId += 1
  }

  public disconnectedCallback(): void {
    if (!(this.#referenceElement instanceof globalThis.Element)) return

    this.#removeEventListeners()
    this.#referenceElement.removeAttribute('aria-describedby')
  }

  public render() {
    return (
      <Host
        onKeyDown={this.#boundKeyDown}
        onPointerDown={this.#boundTooltipPointerDown}
      >
        <div class="qds-tooltip">
          <slot />
        </div>

        <div class="qds-arrow" ref={this.#arrowRef} />
      </Host>
    )
  }

  readonly #arrowRef = (div?: HTMLElement): void => {
    this.#arrow = div
  }

  readonly #boundTooltipPointerDown = (): void => {
    this.#clickedTooltip = true
  }

  async #handleFocus(): Promise<void> {
    if (!this.disabled) await this.show()
  }

  readonly #boundFocus = (): void => {
    ignorePromise(this.#handleFocus())
  }

  async #handleKeyDown(event: Event): Promise<void> {
    if (event instanceof KeyboardEvent && event.key === 'Escape')
      await this.close()
  }

  readonly #boundKeyDown = (event: Event): void => {
    ignorePromise(this.#handleKeyDown(event))
  }

  async #handlePointerenter(): Promise<void> {
    if (!this.disabled) await this.show()
  }

  readonly #boundPointerenter = (): void => {
    ignorePromise(this.#handlePointerenter())
  }

  async #handlePointerleave(event: Event): Promise<void> {
    if (
      !this.#clickedTooltip &&
      document.activeElement !== this.#referenceElement &&
      document.activeElement?.closest('qds-tooltip') !== this.host &&
      event instanceof PointerEvent &&
      event.relatedTarget !== this.#referenceElement &&
      event.relatedTarget !== this.host
    )
      await this.close()
  }

  readonly #boundPointerleave = (event: Event): void => {
    ignorePromise(this.#handlePointerleave(event))
  }

  async #handleFocusOut(event: Event): Promise<void> {
    if (!(event instanceof FocusEvent)) return
    const { relatedTarget } = event

    if (
      relatedTarget instanceof globalThis.Element &&
      !this.host.contains(relatedTarget) &&
      this.#referenceElement !== relatedTarget
    )
      await this.close()
  }

  readonly #boundFocusout = (event: Event): void => {
    ignorePromise(this.#handleFocusOut(event))
  }

  async #handlePointerdown(event: Event): Promise<void> {
    const composedPath = event.composedPath()
    const withinBounds =
      composedPath.includes(this.host) ||
      (this.#referenceElement instanceof globalThis.Element &&
        composedPath.includes(this.#referenceElement))
    if (!withinBounds) await this.close()
  }

  readonly #boundPointerdown = (event: Event): void => {
    ignorePromise(this.#handlePointerdown(event))
  }

  async #handleReferenceClick(): Promise<void> {
    if (!this.disabled) await (this.#open ? this.close() : this.show())
  }

  readonly #boundReferenceClick = (): void => {
    ignorePromise(this.#handleReferenceClick())
  }

  #on(
    element: Element,
    event: keyof GlobalEventHandlersEventMap,
    listener: EventListenerOrEventListenerObject,
  ): void {
    const hasListener = this.#listeners.some(
      (value) => value.element === element && value.event === event,
    )
    if (hasListener) return

    element.addEventListener(event, listener)
    this.#listeners.push({ element, event, listener })
  }

  #removeEventListeners(): void {
    for (const { element, event, listener } of this.#listeners)
      element.removeEventListener(event, listener)
    this.#listeners = []
  }

  #setAriaExpanded(): void {
    this.host.hidden = !this.#open
    if (this.#open) this.host.showPopover()
    else this.host.hidePopover()

    if (
      this.#referenceElement instanceof globalThis.Element &&
      this.host.role === 'dialog'
    )
      this.#referenceElement.ariaExpanded = String(this.#open)
  }
}
